import { init, addIntegration, lazyLoadIntegration, breadcrumbsIntegration, browserTracingIntegration, captureMessage } from "@sentry/browser";

// The landing (apex) host of every brand, and its courses host learn.<apex>.
// Any host missing here reports nothing at all: init() never runs, so
// getClient() stays undefined and every capture becomes a no-op.
const LANDING_HOSTS = ['drv.ge', 'drv.am', 'drv.md', 'preparapatente.it', 'lapermis.ro', 'drv.mk'];
const LEARN_HOSTS = LANDING_HOSTS.map((host) => `learn.${host}`);

// Google's iOS apps -- Chrome ("CriOS") and the Google app ("GSA") -- inject
// their own minified scripts into every page, and those overflow the stack
// ("RangeError: Maximum call stack size exceeded.") or throw "Error: La" on ours:
// ~700 events in 90 days over 15 issues, never from Safari. Their frames carry
// the page's URL (so Sentry quoted whatever line it fetched there, e.g. the
// sign-in form's "Security check") and the same positions on every page, so
// denyUrls cannot catch them. Our code always has a frame in one of our scripts
// (/assets/); an exception from those apps with none is theirs.
const GOOGLE_IOS_APP = /\b(CriOS|GSA)\//;

const isGoogleIosAppScriptError = (event) => {
  if (!GOOGLE_IOS_APP.test(navigator.userAgent)) return false;

  const frames = (event.exception?.values || []).flatMap((value) => value.stacktrace?.frames || []);
  return frames.length > 0 && !frames.some((frame) => (frame.filename || '').includes('/assets/'));
};

// Initialize Sentry for frontend error tracking
// Only enabled on the production brand hosts listed above
const initializeSentry = () => {
  const hostname = window.location.hostname;
  const isLearnHost = LEARN_HOSTS.includes(hostname);
  const isProduction = isLearnHost || LANDING_HOSTS.includes(hostname);

  // Only initialize in production
  if (isProduction) {
    init({
      dsn: "https://d1483afdd0fc45428913966b9c62b5a7@o617217.ingest.sentry.io/5750730",

      // Transactions, not errors, exhaust the Sentry quota: 8,005 HTTP 429s in
      // 14 days, >85% of events dropped during the worst window (see
      // docs/datadog_production_issues_2026-07-08.md). Sampling them at 5% buys
      // back far more headroom than the three brands added above consume, and
      // Datadog APM owns tracing now anyway.
      tracesSampleRate: 0.05,

      // Enable session tracking for user journey analysis
      replaysSessionSampleRate: 0.01, // 1% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Set environment to production
      environment: 'production',

      // Enable automatic breadcrumbs
      integrations: [
        breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
          xhr: true,
        }),
        browserTracingIntegration({
          // Track Turbo navigations as transactions
          tracePropagationTargets: ["localhost", /^\//],
        }),
      ],

      // Drop errors thrown by third-party code we don't ship. Cloudflare
      // Zaraz (the tag manager injected at the edge) emits unhandled promise
      // rejections on Mobile Safari — a DOMException SYNTAX_ERR ("The string
      // did not match the expected pattern") from /cdn-cgi/zaraz/s.js. Zero
      // user impact, not fixable from our code. Filter by frame URL because the
      // DOMException message is browser-localized, so ignoreErrors can't catch it.
      denyUrls: [/\/cdn-cgi\/zaraz\//],

      beforeSend: (event) => (isGoogleIosAppScriptError(event) ? null : event),

      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // Network errors that aren't actionable
        'NetworkError',
        'Failed to fetch',
        // Safari's equivalent of "Failed to fetch": a transient mobile
        // connection drop on a fire-and-forget Turbo request (e.g. link
        // prefetch on test_practice). Non-actionable network noise.
        'Load failed',
        // Service-worker boundary form of the same transient failure. Kept
        // even after the SW stopped amplifying it (PwaController v5), because
        // old service workers persist on devices until the new one activates.
        'FetchEvent.respondWith received an error',
        // Turbo cancels its in-flight fetch when a newer navigation, prefetch
        // or form submission supersedes it, and the browser rejects it with a
        // stackless DOMException (ABORT_ERR) in whatever wording it uses: "The
        // user aborted a request.", "Fetch is aborted", "signal is aborted
        // without reason". Our own code never aborts a request.
        'AbortError',
      ],
    });

    // Session Replay runs on the learn.* hosts only: practice, payments and the Android
    // app. To replay errors it buffers every session, and on the apex landing and wiki
    // pages that buffering was the longest task of the page load (132-309 ms on mobile in
    // the lapermis.ro audit of 2026-10-26, growing with the DOM), all to replay static
    // reference pages. Errors there still report with their breadcrumbs, just no replay.
    //
    // replay.min.js is ~154 KB (~50 KB gzipped) and samples 1% of sessions, so it is not
    // in the bundle at all: Sentry's lazyLoadIntegration fetches it from the Sentry CDN
    // once the page is idle and addIntegration wires it in. Errors before that still
    // report; only the replay of those first moments is missing.
    if (isLearnHost) {
      const attachReplay = () =>
        lazyLoadIntegration("replayIntegration").then((replayIntegration) => addIntegration(replayIntegration()))
          .catch(() => {})
      if ("requestIdleCallback" in window) requestIdleCallback(attachReplay); else setTimeout(attachReplay, 2000);
    }

    // Minimal Turbo error tracking - capture frame failures as events.
    // The status/url live on event.detail.response (a Fetch Response); the old
    // event.detail.fetchResponse.response.url path was always undefined, so every
    // event logged "unknown". Capturing the real status surfaces the dominant
    // 422 (expired-CSRF) cause vs the transient 5xx minority. turbo_frame_recovery
    // also listens and reloads to recover the practice form.
    document.addEventListener('turbo:frame-missing', (event) => {
      const frameId = event.target?.id || 'unknown';
      const response = event.detail?.response;
      const status = response?.status || 'unknown';
      const url = response?.url || 'unknown';
      captureMessage(`Turbo frame missing: ${frameId} (${status}) at ${url}`, 'warning');
    });

    document.addEventListener('turbo:fetch-request-error', (event) => {
      const status = event.detail?.fetchResponse?.response?.status;
      const url = event.detail?.fetchResponse?.response?.url || 'unknown';
      if (status && status >= 400) {
        captureMessage(`Turbo fetch error: ${status} at ${url}`, status >= 500 ? 'error' : 'warning');
      }
    });
  }
};

// Initialize on import
initializeSentry();
